import React from 'react';

import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import { makeStyles } from '@material-ui/core/styles';
import { withRouter } from 'react-router-dom';

import * as colors from '../theme/colors';

// Routes are code-split, so a chunk can fail to load: a network blip, or a
// deploy replacing the hashed filenames while the tab stayed open. Without a
// boundary React unmounts the tree and the user is left on a blank page.
export const isChunkLoadError = (error) => {
  if (!error) return false;

  return (
    error.name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [^\s]+ failed/i.test(error.message || '')
  );
};

// A stale chunk fixes itself on reload, so heal it once rather than showing an
// error for something the app can recover from. The flag stops a reload loop
// when the chunk is genuinely gone.
const RELOAD_FLAG = 'routeChunkReloaded';

const hasAlreadyReloaded = () => {
  try {
    return window.sessionStorage.getItem(RELOAD_FLAG) === '1';
  } catch (e) {
    // Private mode / blocked storage: fall through to the visible fallback.
    return true;
  }
};

const markReloaded = () => {
  try {
    window.sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch (e) {
    // Nothing to do — the reload just will not be attempted again.
  }
};

export const clearReloadFlag = () => {
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch (e) {
    // Ignore: the flag only guards against a reload loop.
  }
};

const useStyles = makeStyles(() => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    minHeight: '60vh',
    textAlign: 'center',
    color: colors.darkBlue900,
  },
  button: {
    backgroundColor: colors.darkThemeGreen,
    color: colors.white,
    '&:hover': {
      backgroundColor: colors.darkThemeGreen,
    },
  },
}));

const RouteErrorFallback = ({ onReload }) => {
  const classes = useStyles();

  return (
    <Container className={classes.wrapper} role="alert">
      <h1>Oops! This page isn’t loading.</h1>
      <p>No worries! Try refreshing the page to try again.</p>
      <Button className={classes.button} onClick={onReload} variant="contained">
        Refresh Page
      </Button>
    </Container>
  );
};

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error)) {
      if (!hasAlreadyReloaded()) {
        markReloaded();
        window.location.reload();
      }
      return;
    }

    // Not self-healing — surface it so it reaches the browser console.
    // eslint-disable-next-line no-console
    console.error('Route failed to render', error);
  }

  // The fallback renders off state, so without this a nav click changes the URL
  // and leaves the user stuck on the error screen. Keying off the previous error
  // skips the route that threw on arrival — it was never showing the fallback.
  componentDidUpdate(prevProps, prevState) {
    const { location } = this.props;

    if (prevState.error && location !== prevProps.location) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ error: null });
    }
  }

  handleReload() {
    clearReloadFlag();
    window.location.reload();
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;

    if (!error) return children;

    return <RouteErrorFallback onReload={this.handleReload} />;
  }
}

export { RouteErrorBoundary };

export default withRouter(RouteErrorBoundary);
